import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import actionPlansService from "@services/actionPlansService";
import actionPlanTasksService from "@services/actionPlanTasksService";
import actionPlanLinksService from "@services/actionPlanLinksService";
import actionPlanDriversService from "@services/actionPlanDriversService";

const PAGE_SIZE = 10;

// Maps API field names → legacy field names used in existing components
export function fromApiItem(item) {
  return {
    id: item.id,
    _apiId: item.id,
    titulo: item.title || "",
    desc: item.description || "",
    etapa: item.indicator || "NPS Geral",
    tipo: "nps",
    v_atual: item.current_value ?? 0,
    v_baseline: item.current_value ?? 0,
    v_meta: item.target_value ?? 0,
    resp: item.responsible || "",
    prazo: item.due_date || "",
    prio: item.priority || "media",
    status: item.status || "pendente",
    check: (item.tasks || []).map((t) => ({
      i: t.id,
      t: t.description,
      f: t.completed,
      inicio: t.start_date || null,
      fim: t.end_date || null,
      concluido: t.completed ? item.updated_at : null,
    })),
    links: (item.links || []).map((l) => ({
      id: l.id,
      label: l.name || l.url,
      url: l.url,
    })),
    drivers: (item.drivers || []).map((d) => d.driver_name),
    hist: [],
    med: [],
    sucesso: null,
    origem: "API",
  };
}

// Maps legacy field names → API payload for create
function toApiPayload(f) {
  return {
    title: f.titulo || "",
    description: f.desc || null,
    indicator: f.etapa || "NPS Geral",
    current_value: f.v_atual != null ? Number(f.v_atual) : null,
    target_value: f.v_meta != null ? Number(f.v_meta) : null,
    responsible: f.resp || null,
    due_date: f.prazo || null,
    priority: f.prio || "media",
    status: f.status || "em_andamento",
  };
}

// Maps UI status filter values → API status param
function uiStatusToApi(statuses) {
  const apiStatuses = new Set();
  for (const s of statuses) {
    if (s === "concluido_geral" || s === "concluido_sucesso") {
      apiStatuses.add("concluido");
    } else if (["pendente", "em_andamento", "cancelado"].includes(s)) {
      apiStatuses.add(s);
    }
    // "atrasado", "arquivado" have no direct API equivalent — filtered client-side
  }
  return Array.from(apiStatuses);
}

export const fetchActionPlans = createAsyncThunk(
  "actionPlans/fetch",
  async ({ page = 1, search, statuses = [], responsible } = {}) => {
    const apiStatuses = uiStatusToApi(statuses);
    const params = {
      page,
      pageSize: PAGE_SIZE,
      title: search || undefined,
      // API supports single status — send first mapped value or undefined
      status: apiStatuses.length === 1 ? apiStatuses[0] : undefined,
      responsible: responsible || undefined,
    };
    const result = await actionPlansService.get(params);
    return { ...result, page };
  },
);

export const fetchActionPlanOverview = createAsyncThunk(
  "actionPlans/fetchOverview",
  async () => {
    const result = await actionPlansService.getOverview();
    return result;
  },
);

export const createActionPlan = createAsyncThunk(
  "actionPlans/create",
  async (formData, { dispatch }) => {
    const payload = toApiPayload(formData);
    const result = await actionPlansService.post(payload);
    const newPlan = result?.data;
    if (!newPlan?.id) return null;

    const promises = [];

    if (formData.check?.length > 0) {
      formData.check.forEach((c) => {
        promises.push(
          actionPlanTasksService.post({
            fk_id_action_plan: newPlan.id,
            description: c.t,
            start_date: c.inicio || null,
            end_date: c.fim || null,
            completed: c.f || false,
          }),
        );
      });
    }

    if (formData.links?.length > 0) {
      formData.links.forEach((l) => {
        promises.push(
          actionPlanLinksService.post({
            fk_id_action_plan: newPlan.id,
            name: l.label || null,
            url: l.url,
          }),
        );
      });
    }

    if (formData.drivers?.length > 0) {
      formData.drivers.forEach((d) => {
        promises.push(
          actionPlanDriversService.post({
            fk_id_action_plan: newPlan.id,
            driver_name: typeof d === "string" ? d : d.driver_name || d,
          }),
        );
      });
    }

    await Promise.allSettled(promises);

    dispatch(fetchActionPlanOverview());

    return newPlan;
  },
);

export const deleteActionPlan = createAsyncThunk(
  "actionPlans/delete",
  async (id) => {
    await actionPlansService.delete(id);
    return id;
  },
);

export const updateActionPlan = createAsyncThunk(
  "actionPlans/update",
  async (formData, { getState, dispatch, rejectWithValue }) => {
    const planId = formData._apiId || Number(formData.id);

    try {
      await actionPlansService.put(planId, toApiPayload(formData));
    } catch (err) {
      return rejectWithValue(err?.message || "Falha ao salvar plano de ação");
    }

    const state = getState();
    const existingPlan = state.actionPlans.items.find(
      (i) => i.id === planId || i.id === Number(planId),
    );
    const existingTaskIds = new Set((existingPlan?.check || []).map((c) => c.i));
    const existingLinkIds = new Set(
      (existingPlan?.links || []).filter((l) => l.id).map((l) => l.id),
    );

    const promises = [];

    // Task sync: update existing, create new, delete removed
    const formTaskIds = new Set();
    (formData.check || []).forEach((c) => {
      formTaskIds.add(c.i);
      if (existingTaskIds.has(c.i)) {
        promises.push(
          actionPlanTasksService.put(c.i, {
            completed: c.f || false,
            start_date: c.inicio || null,
            end_date: c.fim || null,
          }),
        );
      } else {
        promises.push(
          actionPlanTasksService.post({
            fk_id_action_plan: planId,
            description: c.t,
            completed: c.f || false,
            start_date: c.inicio || null,
            end_date: c.fim || null,
          }),
        );
      }
    });
    (existingPlan?.check || []).forEach((c) => {
      if (!formTaskIds.has(c.i)) {
        promises.push(actionPlanTasksService.delete(c.i));
      }
    });

    // Link sync: create new, delete removed
    const formLinkIds = new Set();
    (formData.links || []).forEach((l) => {
      if (l.id) formLinkIds.add(l.id);
      if (!l.id || !existingLinkIds.has(l.id)) {
        promises.push(
          actionPlanLinksService.post({
            fk_id_action_plan: planId,
            name: l.label || null,
            url: l.url,
          }),
        );
      }
    });
    (existingPlan?.links || []).forEach((l) => {
      if (l.id && !formLinkIds.has(l.id)) {
        promises.push(actionPlanLinksService.delete(l.id));
      }
    });

    // Driver sync: fetch fresh IDs from API, diff against form, delete/create as needed
    const fresh = await actionPlansService.getById(planId);
    const freshDrivers = fresh?.data?.drivers || [];
    const currentDriverNames = new Set(freshDrivers.map((d) => d.driver_name));
    const formDriverNames = new Set(
      (formData.drivers || []).map((d) =>
        typeof d === "string" ? d : d.driver_name || d,
      ),
    );
    freshDrivers.forEach((d) => {
      if (!formDriverNames.has(d.driver_name)) {
        promises.push(actionPlanDriversService.delete(d.id));
      }
    });
    (formData.drivers || []).forEach((name) => {
      const n = typeof name === "string" ? name : name.driver_name || name;
      if (!currentDriverNames.has(n)) {
        promises.push(
          actionPlanDriversService.post({
            fk_id_action_plan: planId,
            driver_name: n,
          }),
        );
      }
    });

    await Promise.allSettled(promises);

    dispatch(fetchActionPlanOverview());

    const refreshed = await actionPlansService.getById(planId);
    return refreshed?.data || null;
  },
);

const actionPlansSlice = createSlice({
  name: "actionPlans",
  initialState: {
    items: [],
    page: 1,
    totalItems: 0,
    hasMore: false,
    status: "idle",
    createStatus: "idle",
    updateError: null,
    overview: {
      total: 0,
      concluidas: 0,
      em_andamento: 0,
      atrasadas: 0,
      status: "idle",
    },
  },
  reducers: {
    resetActionPlans: (state) => {
      state.items = [];
      state.page = 1;
      state.totalItems = 0;
      state.hasMore = false;
      state.status = "idle";
    },
    removeActionPlan: (state, action) => {
      state.items = state.items.filter((i) => i.id !== action.payload);
      state.totalItems = Math.max(0, state.totalItems - 1);
      state.overview.total = Math.max(0, (state.overview.total || 0) - 1);
    },
    updateActionPlanLocal: (state, action) => {
      const updated = action.payload;
      state.items = state.items.map((i) =>
        i.id === updated.id ? { ...i, ...updated } : i,
      );
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchActionPlans.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchActionPlans.fulfilled, (state, action) => {
        const { data, pagination, page } = action.payload;
        const items = (data || []).map(fromApiItem);
        state.items = page === 1 ? items : [...state.items, ...items];
        state.page = page;
        state.totalItems = pagination?.totalItems ?? 0;
        state.hasMore = pagination?.hasNext ?? false;
        state.status = "succeeded";
      })
      .addCase(fetchActionPlans.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(fetchActionPlanOverview.pending, (state) => {
        state.overview.status = "loading";
      })
      .addCase(fetchActionPlanOverview.fulfilled, (state, action) => {
        const d = action.payload?.data || action.payload || {};
        state.overview = {
          total: d.total ?? 0,
          concluidas: d.concluidas ?? 0,
          em_andamento: d.em_andamento ?? 0,
          atrasadas: d.atrasadas ?? 0,
          status: "succeeded",
        };
      })
      .addCase(fetchActionPlanOverview.rejected, (state) => {
        state.overview.status = "failed";
      })
      .addCase(createActionPlan.pending, (state) => {
        state.createStatus = "loading";
      })
      .addCase(createActionPlan.fulfilled, (state, action) => {
        state.createStatus = "succeeded";
        if (action.payload) {
          const newItem = fromApiItem(action.payload);
          state.items = [newItem, ...state.items];
          state.totalItems += 1;
        }
      })
      .addCase(createActionPlan.rejected, (state) => {
        state.createStatus = "failed";
      })
      .addCase(deleteActionPlan.fulfilled, (state, action) => {
        const id = action.payload;
        state.items = state.items.filter((i) => i.id !== id);
        state.totalItems = Math.max(0, state.totalItems - 1);
        state.overview.total = Math.max(0, (state.overview.total || 0) - 1);
      })
      .addCase(updateActionPlan.pending, (state) => {
        state.updateError = null;
      })
      .addCase(updateActionPlan.fulfilled, (state, action) => {
        state.updateError = null;
        const updated = action.payload;
        if (updated) {
          const mapped = fromApiItem(updated);
          state.items = state.items.map((i) => {
            if (i.id !== mapped.id) return i;
            // sucesso is local-only (no DB column) — preserve it across refreshes
            return { ...mapped, sucesso: i.sucesso };
          });
        }
      })
      .addCase(updateActionPlan.rejected, (state, action) => {
        state.updateError = action.payload || "Erro ao atualizar plano";
      });
  },
});

export const { resetActionPlans, removeActionPlan, updateActionPlanLocal } =
  actionPlansSlice.actions;

export const selectActionPlansItems = (state) => state.actionPlans.items;
export const selectActionPlansStatus = (state) => state.actionPlans.status;
export const selectActionPlansHasMore = (state) => state.actionPlans.hasMore;
export const selectActionPlansPage = (state) => state.actionPlans.page;
export const selectActionPlanOverview = (state) => state.actionPlans.overview;
export const selectActionPlanCreateStatus = (state) =>
  state.actionPlans.createStatus;
export const selectActionPlanUpdateError = (state) =>
  state.actionPlans.updateError;

export default actionPlansSlice.reducer;
